import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";

export abstract class CustomValidator {
  // set mark as touch given form group
  public static markFormGroupTouched(formGroup: FormGroup): void {
    (<any>Object).values(formGroup.controls).forEach((control: any) => {
      control.markAsTouched();

      if (control.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }

  public static lessThanSlotCountValidator(slotCount: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = Number(control.value);
      if (!control.value) return null; // No error if empty (handled by 'required')

      if (value <= 0) {
        return { lessThanOrEqualToZero: true }; // Error when value is <= 0
      }

      if (value > slotCount) {
        return { exceedsSlotCount: { max: slotCount - 1 } }; // Error when value >= slotCount
      }

      return null; // No errors if valid
    };
  };

  public static validateGreaterThanZero(control: FormControl) {
    const value = control.value;
    if (value <= 0) {
      return { lessThanZero: true };
    }
    return null;
  }

  public static validateGreaterThanZero1(control: AbstractControl): ValidationErrors | null {
    const value = Number(control.value);
    return value > 0 ? null : { validateGreaterThanZero: true };
  }
  public static validateLessThanHundred(control: FormControl) {
    const value = control.value;
    if (value > 100) {
      return { lessThanHundred: true };
    }
    return null;
  }

  public static validateLessThanThousand(control: FormControl) {
    const value = control.value;
    if (value > 999) {
      return { lessThanThousand: true };
    }
    return null;
  }
}

export const passwordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null; // Return null if no value (assuming required validator handles this)
  }

  const value: string = control.value;
  // Check for required character types
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value); // Add more special characters as needed

  // Check if all required character types are present
  const isValid =
    hasUppercase &&
    hasLowercase &&
    hasNumber &&
    hasSpecialChar &&
    value.length >= 8 && // Minimum length
    value.length <= 15; // Maximum length

  if (!isValid) {
    return {
      invalidPassword: true,
      missingUppercase: !hasUppercase,
      missingLowercase: !hasLowercase,
      missingNumber: !hasNumber,
      missingSpecialChar: !hasSpecialChar,
      minLength: value.length < 8,
      maxLength: value.length > 15
    };
  }
  return null;
}

export function validateInput(event: KeyboardEvent): boolean {
  const allowedChars = /[0-9\.]/;
  const currentValue: string = (event.target as HTMLInputElement).value;
  const cursorPosition: number = (event.target as HTMLInputElement).selectionStart || 0;

  // Check if the pressed key is allowed (digit or dot)
  if (!allowedChars.test(event.key)) {
    return false;
  }

  // If the pressed key is a dot
  if (event.key === '.') {
    // Check if there's already a dot in the current value or if the cursor is at the start
    if (currentValue.includes('.') || cursorPosition === 0) {
      return false;
    }

    // Check if the current value is exactly '100'
    if (currentValue === '100') {
      return false;
    }
  }

  // If the current value already contains a dot
  if (currentValue.includes('.')) {
    // Check if the cursor is after the dot and if adding another digit would exceed two decimal places
    const dotPosition = currentValue.indexOf('.');
    const decimalPlaces = currentValue.length - dotPosition - 1;
    if (cursorPosition > dotPosition && decimalPlaces >= 2) {
      return false;
    }
  }

  return true; // Allow the input
}

export function onInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  // Remove numeric characters from the input value
  input.value = input.value.replace(/[0-9]/g, '');
}

export function emailOrPhoneValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value) {
    return { required: true };
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // Email pattern
  const phoneRegex = /^\d{10}$/; // 10-digit phone number

  if (phoneRegex.test(value)) {
    return null; // ✅ Valid phone number
  } else if (/[a-zA-Z]/.test(value)) {
    // User entered letters, apply email validation
    return emailRegex.test(value) ? null : { invalidEmail: true };
  } else {
    // User entered non-numeric characters but not an email format
    return { invalidPhone: true };
  }
}